import "./style.css";
import { useEffect, useState, useCallback } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AgGridReact } from "ag-grid-react";
import { Link } from "react-router-dom";
import debounce from "lodash/debounce";
import { Col, Row, Form } from "react-bootstrap";
import { FiDownload } from "react-icons/fi";
import { MdClear } from 'react-icons/md';
import Button from "@mui/material/Button";
import Widgets from "../../../components/Widgets";
import {
  commonGetData,
  commonSaveData,
  initCommonState,
  initState,
} from "../../../redux/slices/commonSlice";
import {
  getEmployeeBankListPath,
  deactivateEmployeePath,
} from "../../../services/config";
import { useGlobalToast } from '../../../stopOldToastGlobally.js';
import CustomPagination from "../../../components/CustomPagination";
import { IoEyeOutline } from "react-icons/io5";
import { PiPencilSimpleLineBold } from "react-icons/pi";
import {
  getCurrentDate,
  getInitials,
  getInitialColor,
} from "../../../services/commonServices";
import { HasPermission } from "../../../services/hasPermission";
import TableSkeleton from "../../../components/table/TableSkeleton.js";
import * as XLSX from "xlsx";

// ============================================================
// BANK DOWNLOAD HEADERS (exact order from document)
// ============================================================
const BANK_HEADERS = [
  "Sl No",
  "Employee ID",
  "Employee Name",
  "Department",
  "Designation",
  "Branch",
  "Name as in Account",
  "Account Number",
  "Account Type",
  "Bank Name",
  "Bank Branch",
  "IFSC Code",
  "Document (File)",
];

// Map raw bank row → flat object matching BANK_HEADERS
const mapBankRow = (item, index) => ({
  "Sl No":               index + 1,
  "Employee ID":         item.employeeId   || item.employee_id  || "-",
  "Employee Name":       item.name         || item.full_name    || "-",
  "Department":          item.departmentName || item.department || "-",
  "Designation":         item.job_title    || item.designation  || "-",
  "Branch":              item.branchName   || item.branch       || "-",
  "Name as in Account":  item.accountHolderName || item.account_holder_name || "-",
  "Account Number":      item.accountNumber || item.account_number || "-",
  "Account Type":        item.accountType == 1 ? "Saving" : item.accountType == 2 ? "Current" : "-",
  "Bank Name":           item.bankName     || "-",
  "Bank Branch":         item.bankBranch   || "-",
  "IFSC Code":           item.ifscCode     || item.ifsc_code    || "-",
  "Document (File)":     item.bankCheque   || "-",
});

// Excel column widths
const BANK_COL_WIDTHS = [
  { wch: 6 },   // Sl No
  { wch: 14 },  // Employee ID
  { wch: 24 },  // Employee Name
  { wch: 18 },  // Department
  { wch: 20 },  // Designation
  { wch: 16 },  // Branch
  { wch: 24 },  // Name as in Account
  { wch: 20 },  // Account Number
  { wch: 14 },  // Account Type
  { wch: 20 },  // Bank Name
  { wch: 20 },  // Bank Branch
  { wch: 16 },  // IFSC Code
  { wch: 40 },  // Document (File)
];

// CSV builder
const buildCSV = (rows) => {
  const escape = (val) => {
    const str = String(val ?? "-");
    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };
  return [
    BANK_HEADERS.map(escape).join(","),
    ...rows.map((row) => BANK_HEADERS.map((h) => escape(row[h])).join(",")),
  ].join("\n");
};

const BankList = ({ dispatch, user, state, branchList, designationList, departmentList }) => {
   const customToast = useGlobalToast(); 
  const [usersList, setUsersList] = useState([]);
  const [filteredUsersList, setFilteredUsersList] = useState([]);
  const [filterUser, setFilterUser] = useState("");

  const [jobTitle, setJobTitle] = useState("");
  const [branch, setBranch] = useState("0");
  const [departmentId, setDepartmentId] = useState("0");

  const path = `${getEmployeeBankListPath}${user.user_id
    }&branchId=${branch}&departmentId=${departmentId}&jobTitle=${jobTitle}`;

  const [gridApi, setGridApi] = useState(null);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const getEmployeeList = () => {
    setUsersList([]);
    setFilteredUsersList([]);
    dispatch(commonGetData(path));
  };

  useEffect(() => {
    getEmployeeList();
  }, [jobTitle, branch, departmentId, dispatch, path]);

  useEffect(() => {
    if (state.commonGetSuccess) {
      if (state.commonGetData == undefined || state.commonGetData == "") {
        setUsersList([]);
        setFilteredUsersList([]);
        dispatch(initState());
      } else {
        if (filterUser) {
          handleSearch(filterUser, state.commonGetData);
        } else {
          if (state.commonGetData && state.commonGetData[0].name) {
            setUsersList(state.commonGetData);
            setFilteredUsersList(state.commonGetData);
            dispatch(initState());
          }
        }
      }
    }
    if (state.commonGetError) {
      setUsersList([]);
      setFilteredUsersList([]);
      dispatch(initState());
    }
    if (state.commonSuccess) {
      getEmployeeList();
      dispatch(initCommonState());
    }
    if (state.commonError) {
      dispatch(initCommonState());
    }
  }, [state]);

  const handleActiveInactiveChange = (event, rowData) => {
    const data = {
      path: deactivateEmployeePath,
      data: {
        userId: user.user_id,
        staffId: rowData.staffId,
        isActiveUser: event.target.value,
      },
    };
    dispatch(commonSaveData(data));
  };

  const columns = [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const rowIndex = params.node?.rowIndex || 0;
        const api = params.api;
        if (api?.paginationGetCurrentPage && api?.paginationGetPageSize) {
          const currentPage = api.paginationGetCurrentPage() || 0;
          const pageSize = api.paginationGetPageSize() || 1;
          return currentPage * pageSize + rowIndex + 1;
        }
        return rowIndex + 1;
      },
      width: 70,
      pinned: "left",
      headerClass: "text-center",
      cellClass: "text-center",
      cellStyle: {
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px'
      },
    },
    {
      headerName: "Employee ID",
      field: "employeeId",
      width: 120,
      pinned: "left",
      cellStyle: { display: 'flex', alignItems: 'center', padding: '0 8px' },
    },
{
  headerName: "Name",
  field: "name",           // ✅ Add field for proper sorting/filtering
  cellRenderer: (params) => {
    return (
      <Link
        to={`user-info/${params.data.staffId}`}
        className="table-link d-flex align-items-center justify-content-start gap-2"
      >
        <div
          style={{
            minWidth: "30px",   // ✅ minWidth instead of width so it doesn't shrink
            height: "30px",
            border: "1px solid lightgray",
            borderRadius: "50%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: getInitialColor(params.data.name),
            color: "white",
            fontWeight: "bold",
            fontSize: "12px",
            flexShrink: 0,      // ✅ Prevents avatar from squishing
          }}
        >
          {getInitials(params.data.name)}
        </div>
        <span style={{ lineHeight: "1.2" }}>{params.data.name}</span>
      </Link>
    );
  },
  pinned: "left",
  width: 250,              // ✅ Number not string
  autoHeight: true,        // ✅ Let row grow with content
  wrapText: true,          // ✅ Wrap long names
  resizable: true,         // ✅ Allow manual resize
  cellClass: "custom-wrap-cell",
},
    {
      headerName: "Job Title",
      field: "job_title",
      width: "200px",
    },
    {
      headerName: "Branch",
      field: "branchName",
      width: 200,
      resizable: true,
      cellStyle: { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', padding: '6px 8px', display: 'flex', alignItems: 'center' },
    },
    {
      headerName: "Account Type",
      width: "120px",
      cellRenderer: (rowData) => {
        return rowData.data.accountType == 1 ? "Saving" : "Current";
      },
    },
    {
      headerName: "Bank Name",
      width: "150px",
      cellRenderer: (rowData) => {
        return rowData.data.bankName == "" ? "-" : rowData.data.bankName;
      },
    },
    {
      headerName: "Account Number",
      width: "150px",
      cellRenderer: (rowData) => {
        return rowData.data.accountNumber == ""
          ? "-"
          : rowData.data.accountNumber;
      },
    },
    {
      headerName: "IFSC Code",
      width: "150px",
      cellRenderer: (rowData) => {
        return rowData.data.ifscCode == "" ? "-" : rowData.data.ifscCode;
      },
    },
    {
      headerName: "Bank Branch",
      width: "150px",
      cellRenderer: (rowData) => {
        return rowData.data.bankBranch == "" ? "-" : rowData.data.bankBranch;
      },
    },
    {
      headerName: "Attachment",
      width: "110px",
      cellRenderer: (rowData) => {
        return rowData.data.bankCheque ? (
          <Link to={rowData.data.bankCheque} target="_blank">
            <IoEyeOutline style={{ fontSize: "20px" }} className="eye-icon" />
          </Link>
        ) : (
          "-"
        );
      },
    },
    // ============================================================
    // FIX QA #43 — Status column (separate, standalone)
    // Previously the status <select> was jammed inside the Action
    // column renderer alongside the edit icon, which caused it to
    // appear as TWO status buttons (the select + its own visual
    // artefact pushed down by `position:relative; top:9px`).
    // Solution: move Status into its OWN dedicated column so it
    // renders cleanly, and the Action column contains ONLY the
    // edit icon.
    // ============================================================
    {
      headerName: "Status",
      field: "status",
      width: 130,
      cellRenderer: (params) => {
        return (
          <select
            className={
              params.data.status == "1" && params.data.status !== null
                ? "statusGreen"
                : "statusOrange"
            }
            value={params.data.status}
            onChange={(e) => handleActiveInactiveChange(e, params.data)}
          >
            <option value="1">Active</option>
            <option value="0">InActive</option>
          </select>
        );
      },
    },
    {
      headerName: "Action",
      pinned: "right",
      width: 80,
      cellRenderer: (rowData) => {
        return (
          <div className="d-flex align-items-center justify-content-center h-100">
            <Link
              to={`user-info/${rowData.data.staffId}`}
              className="table-link d-flex align-items-center justify-content-center"
              style={{ fontSize: "20px" }}
            >
              <PiPencilSimpleLineBold className="edit-icon" />
            </Link>
          </div>
        );
      },
    },
  ];

  const handleSearch = useCallback(
    debounce((query, dataList) => {
      const debouncedQuery = query.trim().toLowerCase();
      const results = dataList.filter(
        (employee) =>
          (employee.name &&
            employee.name.toLowerCase().includes(debouncedQuery)) ||
          (employee.job_title &&
            employee.job_title.toLowerCase().includes(debouncedQuery)) ||
          (employee.accountType &&
            employee.accountType.toLowerCase().includes(debouncedQuery)) ||
          (employee.bankName &&
            employee.bankName.toLowerCase().includes(debouncedQuery)) ||
          (employee.bankBranch &&
            employee.bankBranch.toLowerCase().includes(debouncedQuery)) ||
          (employee.employeeId &&
            employee.employeeId.toLowerCase().includes(debouncedQuery))
      );
      setFilteredUsersList(results);
      setUsersList(usersList);
    }, 500),
    [usersList]
  );

  const [statusVal, setStatusVal] = useState("");

  const handleSearchByStatus = useCallback(
    debounce((query) => {
      setStatusVal(query);
      const debouncedQuery = query;
      const results = usersList.filter(
        (employee) => employee.status == debouncedQuery
      );
      setFilteredUsersList(results);
    }, 500),
    [usersList]
  );

  const searchUser = (event) => {
    const { target: { value } } = event;
    setFilterUser(value);
    handleSearch(value, usersList);
  };

const downloadBulkData = () => {
  if (!filteredUsersList || filteredUsersList.length === 0) {
    customToast.warning("No data to export");  // ✅ ADD THIS
    return;
  }

  const rows = filteredUsersList.map((item, idx) => mapBankRow(item, idx));
  const csv  = buildCSV(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${getCurrentDate()}_Bank_Detail.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  customToast.success("Bank details downloaded successfully!");  // ✅ ADD THIS
};

  const paginationPageSize = 10;

  const clearFilter = () => {
    setUsersList([]);
    setFilteredUsersList([]);
    getEmployeeList();
    setFilterUser("");
    setJobTitle("");
    setDepartmentId(0);
    setBranch(0);
    setStatusVal("");
  };

  return (
    <div>
      <div className="search-box flex-space-between mt-m-40">
        <Form className="d-flex align-items-center justify-content-start gap-2">
          <Form.Group>
            <Form.Control
              placeholder="Search by Employee Name & ID"
              onChange={searchUser}
              name="filterUser"
              value={filterUser}
              className="universal-input-one"
              style={{ width: "250px" }}
            />
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setJobTitle(e.target.value)}
              name="jobTitle"
              value={jobTitle}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={""}>Select Job Title</option>
              {designationList?.map((row) => {
                return <option value={row.label} key={row.label}>{row.label}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setBranch(e.target.value)}
              name="branch"
              value={branch}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={"0"}>Select Branch</option>
              {branchList?.map((row) => {
                return <option value={row.branchId} key={row.branchId}>{row.branch_name}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setDepartmentId(e.target.value)}
              name="departmentId"
              value={departmentId}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value="0">Select Department</option>
              {departmentList?.map((row) => {
                return <option value={row.value} key={row.value}>{row.label}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => handleSearchByStatus(e.target.value)}
              className="universal-input-one"
              value={statusVal}
              name="statusVal"
              style={{ width: "140px" }}
            >
              <option value="">Select Status</option>
              <option value="1">Active</option>
              <option value="0">InActive</option>
            </Form.Select>
          </Form.Group>

          <Form.Group>
            {
              (filterUser ||
                jobTitle ||
                departmentId != 0 ||
                branch != 0 || statusVal) && <button type="button" className="clear" onClick={clearFilter}>
                <MdClear /> Clear
              </button>
            }
          </Form.Group>
        </Form>
      </div>

      <Widgets
        body={
          <>
            <div
              className="ag-theme-quartz custom-grid"
              style={{ height: "calc(100vh - 310px)", width: "100%" }}
            >
              {state.commonGetLoading ? (
                <TableSkeleton />
              ) : (
                <AgGridReact
                  rowData={Array.isArray(filteredUsersList) ? filteredUsersList : []}
                  columnDefs={columns}
                  pagination={true}
                  paginationPageSize={paginationPageSize}
                  rowHeight={48}
                  defaultColDef={{ sortable: true, resizable: true, suppressMovable: true }}
                  onGridReady={onGridReady}
                />
              )}
            </div>
            <div className="d-flex align-items-center justify-content-between custom-pagination">
              {gridApi && <CustomPagination gridApi={gridApi} />}
              {HasPermission("Bank Details", "export") && (
                <Button
                  onClick={downloadBulkData}
                  className="bulkDataDownloads universal-btn"
                >
                  <FiDownload style={{ fontSize: "16px", marginRight: '10px' }} />
                  Export Data
                </Button>
              )}
            </div>
          </>
        }
      />
    </div>
  );
};

export default BankList;